/**
 * Monthly Calendar Manager
 *
 * Business logic manager for monthly calendar operations.
 * Handles event transformation, date calculations, and data filtering.
 *
 * Follows manager pattern: centralizes business logic separate from UI.
 */

import type { CalendarEvent } from '@/types/events';
import type { CalendarAgendaEvent } from '@/types/calendar-ui';
import { MonthlyCalendarFormatter } from '@/lib/formatters/MonthlyCalendarFormatter';

/**
 * MonthlyCalendarManager
 *
 * Static utility class for monthly calendar business logic.
 */
export class MonthlyCalendarManager {
  /**
   * Get all events for a specific date
   * @param date - Date string in YYYY-MM-DD format
   * @param dateEventMap - Pre-computed map of date → events (pass this for performance)
   */
  static getEventsForDay(date: string, dateEventMap: Map<string, CalendarEvent[]>): CalendarAgendaEvent[] {
    const eventsForDay = dateEventMap.get(date) || [];

    // Transform to CalendarAgendaEvent format with isAllDay computed
    return eventsForDay.map(event => ({
      ...event,
      eventId: event.id,
      isAllDay: event.allDay === true || (!event.startTime && !event.endTime),
    }));
  }

  /**
   * Get adjacent month dates for prefetching
   */
  static getAdjacentMonths(currentDate: string): { prev: string; next: string } {
    const date = this.parseDateString(currentDate);

    // Previous month (15th of previous month)
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 15);
    const prev = this.toDateKey(prevMonth);

    // Next month (15th of next month)
    const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 15);
    const next = this.toDateKey(nextMonth);

    return { prev, next };
  }

  /**
   * Check if a date has events
   */
  static hasEventsOnDate(date: string, allEvents: CalendarEvent[]): boolean {
    const dateEventMap = MonthlyCalendarFormatter.buildDateEventMap(allEvents);
    return dateEventMap.has(date) && (dateEventMap.get(date)?.length || 0) > 0;
  }

  /**
   * Get event count for a specific date
   */
  static getEventCountForDate(date: string, allEvents: CalendarEvent[]): number {
    const dateEventMap = MonthlyCalendarFormatter.buildDateEventMap(allEvents);
    return dateEventMap.get(date)?.length || 0;
  }

  /**
   * Build a map of dates to event counts for efficient lookups
   */
  static buildDateEventCountMap(allEvents: CalendarEvent[]): Map<string, number> {
    const dateEventMap = MonthlyCalendarFormatter.buildDateEventMap(allEvents);
    const countMap = new Map<string, number>();

    dateEventMap.forEach((dayEvents, date) => {
      countMap.set(date, dayEvents.length);
    });

    return countMap;
  }

  /**
   * Filter events for a specific month (for optimization)
   */
  static filterEventsForMonth(allEvents: CalendarEvent[], year: number, month: number): CalendarEvent[] {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0); // Last day of month

    return allEvents.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = event.endDate ? new Date(event.endDate) : eventStart;

      // Check if event overlaps with this month
      return eventStart <= monthEnd && eventEnd >= monthStart;
    });
  }

  /**
   * Get the first day of the month for a given date
   */
  static getMonthStart(date: string): Date {
    const parsed = this.parseDateString(date);
    return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
  }

  /**
   * Get the last day of the month for a given date
   */
  static getMonthEnd(date: string): Date {
    const parsed = this.parseDateString(date);
    return new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0);
  }

  /**
   * Calculate the number of week rows needed to display a month
   * @param year - Full year (e.g., 2026)
   * @param month - Month (0-11, JavaScript Date convention)
   * @returns Number of week rows needed (5 or 6)
   */
  static calculateWeekRows(year: number, month: number): number {
    // Get first day of month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, ...

    // Adjust for Monday as first day (firstDay=1 in react-native-calendars)
    const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Get last day of month
    const lastDate = new Date(year, month + 1, 0).getDate();

    // Calculate total cells needed
    const totalCells = adjustedFirstDay + lastDate;

    // Calculate rows: ceil(totalCells / 7)
    return Math.ceil(totalCells / 7);
  }

  /**
   * Format date for display in event list header
   */
  static formatEventListHeader(date: string): string {
    return MonthlyCalendarFormatter.formatEventListHeader(date);
  }

  /**
   * Truncate event title for text bar display
   */
  static truncateEventTitle(title: string, maxLength: number = 15): string {
    return MonthlyCalendarFormatter.truncateEventTitle(title, maxLength);
  }

  /**
   * Get events to display as text bars on a day (limited to first N events)
   */
  static getTextBarEventsForDay(
    date: string,
    dateEventMap: Map<string, CalendarEvent[]>,
    maxBars: number = 3
  ): Array<{ title: string; color: string; isTruncated: boolean }> {
    const eventsForDay = this.getEventsForDay(date, dateEventMap);
    const textBars = eventsForDay.slice(0, maxBars).map(event => ({
      title: this.truncateEventTitle(event.title, 15),
      color: '#6B9BD1', // Default blue, could be customized per calendar
      isTruncated: event.title.length > 15,
    }));

    return textBars;
  }

  /**
   * Get overflow count for days with many events
   */
  static getOverflowCount(date: string, dateEventMap: Map<string, CalendarEvent[]>, maxBars: number = 3): number {
    const eventsForDay = this.getEventsForDay(date, dateEventMap);
    const overflow = eventsForDay.length - maxBars;
    return overflow > 0 ? overflow : 0;
  }

  /**
   * Convert Date object to YYYY-MM-DD string
   */
  private static toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Parse YYYY-MM-DD string to Date object
   */
  private static parseDateString(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
}
